import { Link } from "@tanstack/react-router";
import { Info, LifeBuoy } from "lucide-react";

type Variant =
  | "default"
  | "journal"
  | "events"
  | "community"
  | "practice"
  | "board"
  | "assessment"
  | "ai"
  | "therapists";

const COPY: Record<Variant, { title: string; body: string }> = {
  default: {
    title: "Important Information",
    body:
      "Recovery Trauma™ provides educational resources, self-help tools, peer support, and wellbeing content. Recovery Trauma™ is not therapy, medical treatment, or a crisis service, and is not a substitute for professional healthcare, counselling, or emergency support.",
  },
  journal: {
    title: "A note on reflections",
    body:
      "This is a private reflection tool — not medical advice, diagnosis or therapy, and it is not monitored as a crisis service. You remain the expert on your own experience. If something brings up distress, please reach out to a qualified professional or a crisis service.",
  },
  events: {
    title: "About our live events",
    body:
      "Live events are educational and informational in nature. Participation does not create a therapeutic or clinical relationship with the host or with Recovery Trauma™.",
  },
  community: {
    title: "Community is peer support — not professional advice",
    body:
      "Community content reflects personal experiences and opinions only and should not be considered medical, psychological, legal, or professional advice.",
  },
  practice: {
    title: "Before you begin",
    body:
      "These practices are wellbeing and self-help tools, not clinical treatment. Choose what feels appropriate for you and stop if an activity feels unsuitable. Seek qualified support if intense feelings arise.",
  },
  board: {
    title: "About the Recovery Board",
    body:
      "The Recovery Board is an educational, self-paced game — not therapy, a medical assessment or a crisis service. Spaces, cards and tips gently introduce trauma-recovery topics; nothing here diagnoses you or explains why you feel as you do. Every card is optional: you can skip a question or exercise without penalty, and nothing is scored or won. If a topic brings up distress, pause and reach out to a qualified professional or a crisis service.",
  },
  assessment: {
    title: "Educational and self-reflective only",
    body:
      "Questionnaires, scores and results here are for education and self-reflection — they are not a diagnosis, clinical assessment or psychological evaluation, and should not be used to diagnose or rule out any condition.",
  },
  ai: {
    title: "About AI-generated content",
    body:
      "AI-generated information may be inaccurate or unsuitable for your circumstances. It is not medical, psychological or therapeutic advice, and must not be relied on for diagnosis, treatment or crisis support.",
  },
  therapists: {
    title: "Independent practitioners",
    body:
      "Therapists listed here work independently — Recovery Trauma™ does not provide the therapy. Administrative verification is not clinical supervision, endorsement or a guarantee of suitability or outcome.",
  },
};

export function ComplianceNotice({
  variant = "default",
  className = "",
  showCrisisLink = true,
}: {
  variant?: Variant;
  className?: string;
  showCrisisLink?: boolean;
}) {
  const { title, body } = COPY[variant];
  return (
    <aside
      className={`rounded-2xl border border-border/60 bg-muted/30 p-4 text-sm leading-relaxed text-foreground/80 ${className}`}
      role="note"
      aria-label={title}
    >
      <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Info className="h-3.5 w-3.5" /> {title}
      </div>
      <p>{body}</p>
      <p className="mt-2 text-xs text-muted-foreground">
        <Link to="/disclaimer" className="underline-offset-2 hover:underline">
          Read the full disclaimer
        </Link>
      </p>
      {showCrisisLink && (
        <p className="mt-2">
          <Link
            to="/crisis"
            className="inline-flex items-center gap-1.5 font-medium text-foreground underline-offset-2 hover:underline"
          >
            <LifeBuoy className="h-3.5 w-3.5" /> Crisis Resources
          </Link>
        </p>
      )}
    </aside>
  );
}
